import { fetchAddressValidationQueue } from '@/hooks/useAddressValidation';
import { sortRows, dateValue, type SortDir } from '@/components/ui/sortable-table';
import type { AddressValidationStatus, AddressValidationQueueItem } from '@uniticket/shared';

// ── The validator's queue state, shared by the queue AND the validation window ───────────────────
// The view survives leaving the page (user ask 2026-08-03) and, since the 04-08 meeting, it must
// also drive the « Ticket suivant » chain: the window used to compute the next ticket from a
// HARDCODED 'PENDING' listing, so a validator working the « À traiter » view was thrown out of it at
// the first hand-off (« il m'a sorti de ma liste à traiter »). One storage key, one fetcher, both
// screens — so the two can never drift apart again.
//
// 2026-08-10, réunion qualité — the same complaint came back about the REST of the state. Anouk
// (l. 1508): « quand je fais suivant […] je tombais sur les tickets à moi. Mais c'est parce qu'en
// fait quand tu fais suivant, il ne suit pas cette liste-là, il suit celle d'origine avant les
// filtres » — her own rows float to the top of the SERVER order (F11, 2026-08-07) while her screen
// was sorted by date, so every hand-off dropped her back onto her own files. And (l. 1172) « il ne
// sauvegarde pas mon filtre sur la date. Ça, c'est chiant » — the sort died the moment she came back
// from a ticket. One cause, one fix: the column sort and the search text live here beside the view,
// the queue writes them, the window reads them back.
//
// ⚠️ THE INVARIANT, for whoever touches this next: a list is a FETCH plus an ORDER. Any screen that
// walks the reviewer's queue must go through BOTH `fetchQueueForView` (with the whole state — same
// view, same « à moi », same search, same includeTest) AND `sortQueueRows`. One without the other is
// exactly the bug Anouk described.

export type QueueView = AddressValidationStatus | 'ALL' | 'TICKET_RESOLVED' | 'FAVORITES';

export const QUEUE_VIEW_STORAGE_KEY = 'addressval-queue-filter';

export const QUEUE_VIEWS: QueueView[] = [
  'PENDING', 'VALIDATED', 'REJECTED', 'ESCALATED', 'WAITING_BUILD', 'TICKET_RESOLVED', 'FAVORITES', 'ALL',
];

// ── ⭐ La personne dont on regarde les favoris ────────────────────────────────────────────────────
// Elle vit ICI, à côté de la vue, et pas dans l'état local de la page — pour la raison exacte qui a
// fait naître ce fichier : « Ticket suivant » doit suivre la liste AFFICHÉE. Si le propriétaire
// consulté restait dans la page, la fenêtre de validation recalculerait le ticket suivant depuis MES
// favoris alors que l'écran montre ceux de Jonathan, et l'enchaînement sauterait d'une liste à l'autre
// — la même famille de bug que les deux plaintes d'Anouk citées plus haut.
// null = les miens.
export const QUEUE_FAV_OWNER_STORAGE_KEY = 'addressval-queue-fav-owner';

export function readQueueFavOwner(): string | null {
  try { return localStorage.getItem(QUEUE_FAV_OWNER_STORAGE_KEY) || null; } catch { return null; }
}

export function saveQueueFavOwner(id: string | null): void {
  try {
    if (id) localStorage.setItem(QUEUE_FAV_OWNER_STORAGE_KEY, id);
    else localStorage.removeItem(QUEUE_FAV_OWNER_STORAGE_KEY);
  } catch { /* best-effort */ }
}

/** The saved view, or the default. Unknown/legacy values fall back rather than throw. */
export function readQueueView(): QueueView {
  try {
    const saved = localStorage.getItem(QUEUE_VIEW_STORAGE_KEY);
    if (saved && QUEUE_VIEWS.includes(saved as QueueView)) return saved as QueueView;
  } catch { /* storage unavailable → default */ }
  return 'PENDING';
}

export function saveQueueView(view: QueueView): void {
  try { localStorage.setItem(QUEUE_VIEW_STORAGE_KEY, view); } catch { /* best-effort */ }
}

// ── « À moi ou à personne » (F11, réunion Fred/Jonathan 2026-08-06) ─────────────────────────────
// « Et avoir un filtre sur soi ou sur personne. Donc quand on commence, on regarde d'abord les
// tickets qui sont chez nous et puis après on continue. » It lives beside the view, and for the same
// reason: the « Ticket suivant » chain must obey it too, otherwise the window would walk the reviewer
// out of the list they are working through. OFF by default — nobody gets a narrowed queue silently.
export const QUEUE_MINE_STORAGE_KEY = 'addressval-queue-mine';

export function readQueueMine(): boolean {
  try { return localStorage.getItem(QUEUE_MINE_STORAGE_KEY) === 'true'; } catch { return false; }
}

export function saveQueueMine(on: boolean): void {
  try { localStorage.setItem(QUEUE_MINE_STORAGE_KEY, on ? 'true' : 'false'); } catch { /* best-effort */ }
}

// ── Column sort (demande Anouk 2026-08-10, l. 1172 et 1508) ─────────────────────────────────────
// Client-side sort on the date columns (born 2026-08-03: sort the waiting view by « Envoyé le » or
// « Création ticket ») — the whole view is already loaded, so a header click costs no round-trip.

export const QUEUE_SORT_STORAGE_KEY = 'addressval-queue-sort';

export type QueueSortKey = 'createdAt' | 'verdictAt' | 'escalatedAt' | 'sentAt' | 'favoritedAt';
export type QueueSort = { key: QueueSortKey; dir: SortDir };

const QUEUE_SORT_KEYS: QueueSortKey[] = ['createdAt', 'verdictAt', 'escalatedAt', 'sentAt', 'favoritedAt'];

/** Which sortable date columns a view actually renders. « Création ticket » is on every view; the
 *  others only exist where they mean something (a verdict date is noise on the pending list). A sort
 *  on a column the view does not show would be an order the reviewer can neither see nor undo, so it
 *  is DROPPED — on restore and on every view change. */
export function queueSortKeyExists(view: QueueView, key: QueueSortKey): boolean {
  switch (key) {
    case 'createdAt': return true;
    case 'verdictAt': return view === 'VALIDATED' || view === 'REJECTED';
    case 'escalatedAt': return view === 'ESCALATED';
    case 'sentAt': return view === 'WAITING_BUILD';
    // ⭐ « Mis en favori le » n'a de valeur que dans la vue Favoris — ailleurs la donnée est nulle, et
    // un tri sur une colonne absente est un ordre que le relecteur ne peut ni voir ni défaire.
    case 'favoritedAt': return view === 'FAVORITES';
  }
}

/** The cell each sortable column reads. 'sentAt' mirrors the « Envoyé le » cell: hand-off date, else
 *  the question date (whichever parked the row). */
export function queueSortValue(it: AddressValidationQueueItem, key: QueueSortKey): string | null {
  switch (key) {
    case 'createdAt': return it.createdAt;
    case 'verdictAt': return it.validatedAt ?? null;
    case 'escalatedAt': return it.escalatedAt ?? null;
    case 'sentAt': return it.handedOffAt ?? it.questionAskedAt ?? null;
    case 'favoritedAt': return it.favoritedAt ?? null;
  }
}

/** THE ordering — the queue table and the window's Previous/Next chain both go through it, which is
 *  what keeps « suivant » on the list the reviewer is looking at. `null` = the server's business
 *  order (escalation, replies, mine, priority, oldest). Rows without the date always sink. */
export function sortQueueRows(
  rows: AddressValidationQueueItem[], sort: QueueSort | null,
): AddressValidationQueueItem[] {
  return sortRows(rows, sort, (it, key) => dateValue(queueSortValue(it, key as QueueSortKey)));
}

/** The saved sort, dropped when it does not apply to `view`. Malformed/legacy values fall back to
 *  the server order rather than throw. */
export function readQueueSort(view: QueueView): QueueSort | null {
  try {
    const raw = localStorage.getItem(QUEUE_SORT_STORAGE_KEY);
    if (!raw) return null;
    const { key, dir } = JSON.parse(raw) as { key?: QueueSortKey; dir?: SortDir };
    if (!key || !QUEUE_SORT_KEYS.includes(key)) return null;
    if (dir !== 'asc' && dir !== 'desc') return null;
    return queueSortKeyExists(view, key) ? { key, dir } : null;
  } catch { return null; }
}

export function saveQueueSort(sort: QueueSort | null): void {
  try {
    if (sort) localStorage.setItem(QUEUE_SORT_STORAGE_KEY, JSON.stringify(sort));
    else localStorage.removeItem(QUEUE_SORT_STORAGE_KEY);
  } catch { /* best-effort */ }
}

// ── Free-text search (jvid / n° de ticket / commentaire) ────────────────────────────────────────
// Restored on return like the view — coming back from a ticket to an unfiltered queue is the very
// irritant the 03-08 ask fixed. Safe to restore because it stays VISIBLE in the search box with its
// clear button: unlike a hidden narrowing, a restored search always says what it is doing (the
// filter-vs-masking line drawn for F5/F10 on 2026-08-06).
export const QUEUE_SEARCH_STORAGE_KEY = 'addressval-queue-search';

/** The APPLIED (debounced) search text — what was actually sent to the server. */
export function readQueueSearch(): string {
  try { return localStorage.getItem(QUEUE_SEARCH_STORAGE_KEY) ?? ''; } catch { return ''; }
}

export function saveQueueSearch(search: string): void {
  try {
    const v = search.trim();
    if (v) localStorage.setItem(QUEUE_SEARCH_STORAGE_KEY, v);
    else localStorage.removeItem(QUEUE_SEARCH_STORAGE_KEY);
  } catch { /* best-effort */ }
}

/** Everything that shapes the list on screen, read in one go — what the window needs to rebuild the
 *  reviewer's exact list. The sort is validated against the restored view, so the two can't disagree. */
export interface QueueState {
  view: QueueView;
  mine: boolean;
  search: string;
  sort: QueueSort | null;
  /** ⭐ De qui on regarde les favoris (null = les miens). Fait partie de l'ÉTAT DE LA LISTE, donc de ce
   *  que « Ticket suivant » doit respecter — voir la note en tête de fichier. */
  favOwnerId: string | null;
}

export function readQueueState(): QueueState {
  const view = readQueueView();
  return {
    view, mine: readQueueMine(), search: readQueueSearch(), sort: readQueueSort(view),
    favOwnerId: readQueueFavOwner(),
  };
}

/** Load one view's rows — the single place that knows how a view maps onto the queue endpoint.
 *  `mine` defaults to the stored preference (what the window's « Ticket suivant » chain wants); the
 *  queue page passes its own state so a toggle reloads without depending on effect ordering.
 *  ⚠️ The rows come back in the SERVER's order — pass them through `sortQueueRows` before treating
 *  them as "the list on screen". */
export function fetchQueueForView(
  view: QueueView,
  includeTest = false,
  search?: string,
  mine: boolean = readQueueMine(),
  favOwnerId: string | null = readQueueFavOwner(),
) {
  // ⭐ « Favoris » est une PSEUDO-VUE, comme « Tous » et « Résolu » : elle n'envoie AUCUN statut. C'est
  // ce qui fait qu'un favori survit au verdict — un statut ici, et le ticket quitterait la liste au
  // moment précis où l'on voulait en parler.
  const isPseudoView = view === 'ALL' || view === 'TICKET_RESOLVED' || view === 'FAVORITES';
  return fetchAddressValidationQueue(
    isPseudoView ? undefined : view,
    includeTest,
    search,
    view === 'TICKET_RESOLVED' ? 'ticketResolved' : view === 'FAVORITES' ? 'favorites' : undefined,
    // The server ignores it on « Tous » (the audit listing stays exhaustive by design).
    mine,
    view === 'FAVORITES' ? favOwnerId : null,
  );
}
